import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Building2, Search, ChevronRight } from "lucide-react";
import UserManageDrawer from "@/components/UserManageDrawer";

const ROLE_BADGE = {
  owner:      { label: "OWNER",       cls: "bg-black text-safety-yellow border-safety-yellow" },
  admin:      { label: "ADMIN",       cls: "bg-safety-yellow text-black border-black" },
  supervisor: { label: "SUPERVISOR",  cls: "bg-blue-600 text-white border-blue-600" },
  employee:   { label: "EMPLOYEE",    cls: "bg-muted text-foreground border-foreground" },
};

export default function OwnerUsers() {
  const { user: me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") === "companies" ? "companies" : "users";
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // user being managed
  const navigate = useNavigate();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/owner/users"),
        api.get("/owner/companies"),
      ]);
      setUsers(u.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.company_name || "").toLowerCase().includes(needle) ||
      (u.role || "").toLowerCase().includes(needle)
    );
  }, [users, q]);

  const filteredCompanies = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return companies;
    return companies.filter((c) =>
      (c.name || "").toLowerCase().includes(needle) ||
      (c.trade || "").toLowerCase().includes(needle) ||
      (c.access_code || "").toLowerCase().includes(needle)
    );
  }, [companies, q]);

  const switchTab = (t) => { setTab(t); setSp(t === "users" ? {} : { tab: "companies" }); };

  // Drawer callback: reflect change in local users array
  const onUserChanged = (updated) => {
    if (updated == null) {
      setUsers((prev) => prev.filter((u) => u.id !== selected.id));
      setSelected(null);
    } else {
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      setSelected(updated);
    }
  };

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading text-3xl md:text-4xl">USERS & COMPANIES</h1>
        <p className="text-sm text-muted-foreground mt-2">Global directory across the platform.</p>
      </div>

      {/* Tabs */}
      <div className="flex border-2 border-foreground dark:border-safety-yellow w-fit mb-6">
        <button
          data-testid="tab-users"
          onClick={() => switchTab("users")}
          className={`h-11 px-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider border-r-2 border-foreground dark:border-safety-yellow transition-colors ${
            tab === "users" ? "bg-safety-yellow text-black" : "bg-transparent hover:bg-safety-yellow/20"
          }`}
        >
          <Users className="w-4 h-4" strokeWidth={3} />
          Users ({users.length})
        </button>
        <button
          data-testid="tab-companies"
          onClick={() => switchTab("companies")}
          className={`h-11 px-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors ${
            tab === "companies" ? "bg-safety-yellow text-black" : "bg-transparent hover:bg-safety-yellow/20"
          }`}
        >
          <Building2 className="w-4 h-4" strokeWidth={3} />
          Companies ({companies.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="owner-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tab === "users" ? "Search by name, email, role, company…" : "Search by company name, trade, code…"}
          className="industrial-input pl-10"
        />
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading…</div>
      ) : tab === "users" ? (
        <UsersTable rows={filteredUsers} onRowClick={(u) => setSelected(u)} />
      ) : (
        <CompaniesTable rows={filteredCompanies} onRowClick={(c) => navigate(`/owner/companies/${c.id}/users`)} />
      )}

      {selected && (
        <UserManageDrawer
          user={selected}
          companies={companies}
          currentOwnerId={me?.id}
          onClose={() => setSelected(null)}
          onChanged={onUserChanged}
        />
      )}
    </div>
  );
}

function UsersTable({ rows, onRowClick }) {
  if (!rows.length) {
    return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider border-2 border-foreground/30 dark:border-safety-yellow/30">No users found.</div>;
  }
  return (
    <div className="border-2 border-foreground dark:border-safety-yellow overflow-x-auto bg-card">
      <table className="w-full text-sm" data-testid="owner-users-table">
        <thead>
          <tr className="bg-foreground/5 dark:bg-safety-yellow/10 border-b-2 border-foreground dark:border-safety-yellow">
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Name</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Email</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Role</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Company</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Status</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Joined</th>
            <th className="px-2 py-3" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => {
            const r = ROLE_BADGE[u.role] || ROLE_BADGE.employee;
            const active = u.active !== false;
            return (
              <tr
                key={u.id}
                role="button"
                tabIndex={0}
                data-testid={`user-row-${u.id}`}
                onClick={() => onRowClick(u)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(u); } }}
                className="border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0 cursor-pointer hover:bg-safety-yellow/10 transition-colors"
              >
                <td className="px-4 py-3 font-bold">{u.name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 text-[10px] font-black uppercase tracking-wider border-2 ${r.cls}`}>
                    {r.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{u.company_name || <span className="text-muted-foreground">— Personal</span>}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-1 text-[10px] font-black uppercase tracking-wider border-2 ${
                      active
                        ? "bg-status-current text-white border-status-current"
                        : "bg-status-expired text-white border-status-expired"
                    }`}
                  >
                    {active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompaniesTable({ rows, onRowClick }) {
  if (!rows.length) {
    return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider border-2 border-foreground/30 dark:border-safety-yellow/30">No companies found.</div>;
  }
  return (
    <div className="border-2 border-foreground dark:border-safety-yellow overflow-x-auto bg-card">
      <table className="w-full text-sm" data-testid="owner-companies-table">
        <thead>
          <tr className="bg-foreground/5 dark:bg-safety-yellow/10 border-b-2 border-foreground dark:border-safety-yellow">
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Company</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Trade</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Access Code</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Users</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Roster</th>
            <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Onboarded</th>
            <th className="px-2 py-3" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr
              key={c.id}
              role="button"
              tabIndex={0}
              data-testid={`company-row-${c.id}`}
              onClick={() => onRowClick(c)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(c); } }}
              className="border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0 cursor-pointer hover:bg-safety-yellow/10 transition-colors"
            >
              <td className="px-4 py-3 font-bold">{c.name}</td>
              <td className="px-4 py-3 text-xs">{c.trade}</td>
              <td className="px-4 py-3">
                <span className="font-mono text-[11px] bg-safety-yellow text-black px-2 py-1 border-2 border-black font-black tracking-widest">
                  {c.access_code}
                </span>
              </td>
              <td className="px-4 py-3 font-bold">{c.user_count}</td>
              <td className="px-4 py-3 font-bold">{c.employee_count}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
              <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
